import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function InvoiceCreatePage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');

  // Customer autocomplete
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Form fields
  const [form, setForm] = useState({
    CustomerName: '',
    GSTIN: '',
    Address: '',
    Phone: '',
    Email: '',
    InvoiceDate: new Date().toISOString().slice(0, 10),
    DueDate: '',
    Notes: '',
    Discount: '0'
  });

  // Invoice items
  const [items, setItems] = useState([{
    Description: '',
    HSN: '',
    Size: '',
    Quantity: '',
    Rate: '',
    GSTPercent: '0',
    Cess: '0',
    CGST: '0',
    SGST: '0',
    CessAmount: '0',
    Amount: '0'
  }]);

  // Product‐search modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [productSearch, setProductSearch]   = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productRowIdx, setProductRowIdx]   = useState(null);

  // Compute subtotals
  const subTotal   = items.reduce((sum, i) => sum + parseFloat(i.Amount || 0), 0);
  const discount   = parseFloat(form.Discount || 0);
  const grandTotal = (subTotal - discount > 0 ? subTotal - discount : 0).toFixed(2);

  // Load customers once
  useEffect(() => {
    api.get('/customers')
       .then(res => {
         setCustomers(res.data);
         setFilteredCustomers(res.data);
       })
       .catch(console.error);
  }, []);

  // Customer autocomplete handlers
  const handleCustomerInput = e => {
    const val = e.target.value;
    setForm(f => ({ ...f, CustomerName: val }));
    setShowSuggestions(true);
    setFilteredCustomers(customers.filter(c =>
      c.Name.toLowerCase().includes(val.toLowerCase())
    ));
  };
  const handlePickCustomer = cust => {
    setForm(f => ({
      ...f,
      CustomerName: cust.Name,
      GSTIN: cust.GSTIN || '',
      Address: cust.Address || '',
      Phone: cust.Phone || '',
      Email: cust.Email || ''
    }));
    setSelectedCustomerId(cust.CustomerID);
    setShowSuggestions(false);
  };

  // Add / remove items
  const addItem = () => {
    setItems(i => ([ ...i, {
      Description: '',
      HSN: '',
      Size: '',
      Quantity: '',
      Rate: '',
      GSTPercent: '0',
      Cess: '0',
      CGST: '0',
      SGST: '0',
      CessAmount: '0',
      Amount: '0'
    } ]));
  };
  const removeItem = idx => {
    if (items.length > 1) setItems(i => i.filter((_,i2)=>i2!==idx));
  };

  // Unified change handler (recomputes CGST/SGST/Cess/Amount)
  const handleChange = (e, idx, field) => {
    const updated = [ ...items ];
    updated[idx][field] = e.target.value;

    // parse values
    const qty        = parseFloat(updated[idx].Quantity)   || 0;
    const rate       = parseFloat(updated[idx].Rate)       || 0;
    const gstPct     = parseFloat(updated[idx].GSTPercent) || 0;
    const cessPct    = parseFloat(updated[idx].Cess)       || 0;

    const baseAmount = qty * rate;
    const cgstAmt    = baseAmount * gstPct/2 / 100;
    const sgstAmt    = baseAmount * gstPct/2 / 100;
    const cessAmt    = baseAmount * cessPct / 100;
    const totalAmt   = baseAmount + cgstAmt + sgstAmt + cessAmt;

    updated[idx].CGST       = cgstAmt.toFixed(2);
    updated[idx].SGST       = sgstAmt.toFixed(2);
    updated[idx].CessAmount = cessAmt.toFixed(2);
    updated[idx].Amount     = totalAmt.toFixed(2);

    setItems(updated);
  };

  // Open product search modal for a row
  const handleOpenProductModal = (rowIdx) => {
    setProductRowIdx(rowIdx);
    setShowProductModal(true);
    setProductSearch('');
    setProductResults([]);
  };

  // Fetch products matching query
  const fetchProductSearch = async q => {
    setProductSearch(q);
    if (!q) { setProductResults([]); return; }
    try {
      const res = await api.get('/products', { params: { search: q } });
      setProductResults(res.data);
    } catch {
      setProductResults([]);
    }
  };

  // Form submit
  const handleSubmit = async e => {
    e.preventDefault();
    try {
      await api.post('/invoices/create', {
        userId,
        CustomerID: selectedCustomerId,
        InvoiceDate: form.InvoiceDate,
        DueDate: form.DueDate,
        Notes: form.Notes,
        Discount: form.Discount || 0,
        Items: items.map(item => ({
          Description: item.Description,
          HSN: item.HSN,
          Size: item.Size,
          Quantity: item.Quantity,
          Rate: item.Rate,
          GSTPercent: item.GSTPercent,
          Cess: item.Cess
        }))
      });
      toast.success('Invoice created successfully');
      setTimeout(() => navigate('/invoices'), 1000);
    } catch {
      toast.error('Error saving invoice');
    }
  };

  // Styles for card & sticky summary bar
  const cardStyle = {
    background: 'rgba(255,255,255,0.92)',
    borderRadius: '2rem',
    boxShadow: '0 2px 16px rgba(40,110,250,0.09)',
    border: '1.5px solid #e1e9f7',
    zIndex: 2,
    maxWidth: 1080,
    margin: '0 auto'
  };
  const stickyBarStyle = {
    background: 'rgba(255,255,255,0.98)',
    borderRadius: '2rem',
    boxShadow: '0 2px 16px rgba(40,110,250,0.09)',
    border: '1.5px solid #e1e9f7',
    position: 'sticky',
    bottom: 10,
    zIndex: 2,
    maxWidth: 980,
    margin: '0 auto'
  };

  return (
    <div className="min-vh-100 d-flex flex-column" style={{
      background: 'linear-gradient(120deg, #a7d1ff 0%, #eaf6ff 100%)',
      paddingBottom: 60
    }}>
      <div className="container py-4 flex-grow-1 d-flex flex-column align-items-center" style={{ minHeight: '100vh' }}>
        <BackButton />
        <h2 className="mb-4 fw-bold text-primary text-center" style={{ letterSpacing: 1 }}>
          <span role="img" aria-label="invoice">🧾</span> Create Invoice
        </h2>

        <div className="card p-4 shadow-lg border-0 rounded-4 w-100 mb-4" style={cardStyle}>
          <form autoComplete="off" onSubmit={handleSubmit}>
            {/* Customer & Date Fields */}
            <div className="row g-3">
              <div className="col-md-6 position-relative">
                <label className="form-label fw-semibold">Customer Name</label>
                <input
                  type="text"
                  className="form-control rounded-pill px-3"
                  placeholder="Type customer name"
                  value={form.CustomerName}
                  onChange={handleCustomerInput}
                  onFocus={()=>setShowSuggestions(true)}
                  required
                />
                {showSuggestions && (
                  <ul className="list-group position-absolute w-100" style={{ zIndex:2200, maxHeight:200, overflowY:'auto' }}>
                    {filteredCustomers.map((c, idx) => (
                      <li
                        key={idx}
                        className="list-group-item list-group-item-action"
                        style={{ cursor:'pointer' }}
                        onMouseDown={()=>handlePickCustomer(c)}
                      >
                        {c.Name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">GSTIN</label>
                <input
                  type="text"
                  className="form-control rounded-pill px-3"
                  value={form.GSTIN}
                  readOnly
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Invoice Date</label>
                <input
                  type="date"
                  className="form-control rounded-pill px-3"
                  value={form.InvoiceDate}
                  onChange={e => setForm({...form, InvoiceDate: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Due Date</label>
                <input
                  type="date"
                  className="form-control rounded-pill px-3"
                  value={form.DueDate}
                  onChange={e => setForm({...form, DueDate: e.target.value})}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Notes</label>
                <textarea
                  className="form-control rounded-3"
                  rows={2}
                  value={form.Notes}
                  onChange={e => setForm({...form, Notes: e.target.value})}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Discount (₹)</label>
                <input
                  type="number"
                  className="form-control rounded-pill px-3"
                  value={form.Discount}
                  onChange={e => setForm({...form, Discount: e.target.value})}
                />
              </div>
            </div>

            {/* ITEMS TABLE */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="fw-semibold mb-0">Invoice Items</h5>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={addItem}>
                  + Add Item
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Description</th>
                      <th>HSN</th>
                      <th>Size</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>GST %</th>
                      <th>CGST</th>
                      <th>SGST</th>
                      <th>Cess %</th>
                      <th>Cess Amt</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <input
                              className="form-control"
                              placeholder="Description"
                              value={item.Description}
                              onChange={e => handleChange(e, idx, 'Description')}
                              required
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleOpenProductModal(idx)}
                            >🔍</button>
                          </div>
                        </td>
                        <td><input className="form-control" value={item.HSN} onChange={e => handleChange(e, idx, 'HSN')} /></td>
                        <td><input className="form-control" value={item.Size} onChange={e => handleChange(e, idx, 'Size')} /></td>
                        <td><input type="number" className="form-control" value={item.Quantity} onChange={e => handleChange(e, idx, 'Quantity')} /></td>
                        <td><input type="number" className="form-control" value={item.Rate} onChange={e => handleChange(e, idx, 'Rate')} /></td>
                        <td><input type="number" className="form-control" value={item.GSTPercent} onChange={e => handleChange(e, idx, 'GSTPercent')} /></td>
                        <td><input className="form-control" value={item.CGST} readOnly /></td>
                        <td><input className="form-control" value={item.SGST} readOnly /></td>
                        <td><input type="number" className="form-control" value={item.Cess} onChange={e => handleChange(e, idx, 'Cess')} /></td>
                        <td><input className="form-control" value={item.CessAmount} readOnly /></td>
                        <td><input className="form-control" value={item.Amount} readOnly /></td>
                        <td>
                          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeItem(idx)} disabled={items.length===1}>
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* SUMMARY BAR */}
            <div className="shadow rounded-4 p-3 mt-4 bg-white d-flex flex-wrap justify-content-between align-items-center" style={stickyBarStyle}>
              <span className="fw-bold">Subtotal: ₹{subTotal.toFixed(2)}</span>
              <span className="fw-bold">Discount: ₹{discount.toFixed(2)}</span>
              <span className="fw-bold">Grand Total: ₹{grandTotal}</span>
            </div>

            <button type="submit" className="btn btn-primary w-100 py-3 rounded-pill fw-bold fs-5 mt-3">
              Save Invoice
            </button>
          </form>
        </div>
      </div>

      {/* -------- Product Search Modal -------- */}
      {showProductModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', background: 'rgba(0,0,0,0.18)', zIndex: 2000 }}
          tabIndex={-1}
          onClick={() => setShowProductModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 480 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-content shadow-lg rounded-3">
              <div className="modal-header">
                <h5 className="modal-title">Search Products</h5>
                <button type="button" className="btn-close" onClick={() => setShowProductModal(false)} />
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Search by name or HSN"
                  value={productSearch}
                  onChange={e => fetchProductSearch(e.target.value)}
                />
                <div className="list-group">
                  {productResults.map(prod => (
                    <button
                      key={prod.ProductID}
                      type="button"
                      className="list-group-item list-group-item-action"
                      onClick={() => {
                        // Populate row with selected product
                        const updated = [...items];
                        updated[productRowIdx] = {
                          ...updated[productRowIdx],
                          Description: prod.Name,
                          HSN: prod.HSN,
                          Rate: prod.Rate.toString(),
                          GSTPercent: prod.GSTPercent.toString()
                        };
                        // Recompute amounts
                        handleChange({ target: { value: updated[productRowIdx].Quantity } }, productRowIdx, 'Quantity');
                        setItems(updated);
                        setShowProductModal(false);
                      }}
                    >
                      <div className="fw-bold">{prod.Name}</div>
                      <div style={{ fontSize: 13 }}>
                        <span>HSN: {prod.HSN} &nbsp; Rate: ₹{prod.Rate} &nbsp; GST: {prod.GSTPercent}%</span>
                      </div>
                    </button>
                  ))}
                  {productResults.length === 0 && productSearch && (
                    <div className="text-center text-muted py-3">No products found.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* toast notifications */}
      <style>{`@media print { .no-print { display: none !important; } }`}</style>
    </div>
  );
}
